import {
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { ChatMessage, ProChat, ChatItemProps } from '@ant-design/pro-chat';
import classNames from 'classnames';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import { ReactComponent as AISvg } from '../../assets/ai.svg';
import { Menu, Tooltip } from 'antd';
import './AdminCopilotChat.less';
import '../ChangeRequestCopilot/ChangeRequestCopilot.less';
import {
  getCopilotChatSessionId,
  setCopilotChatSessionId,
} from '../../services/copilotThread.service';
import {
  ADMIN_AGENT_ASSISTANT_TYPE,
  FAILED_THREAD_ID,
} from '../../constants/adminAgent';
import type { MenuProps } from 'antd';
import { useCopilotChat } from '../../hooks/useCopilotChat';
import { getReadonlyElementTitle } from '../../services/subscription';
import { selectIsSubscriptionReadonly } from '../../app/Slices/subscriptionSlice';
import { useSelector } from 'react-redux';
import { useAgentSelector, useAgentDispatch } from '../../hooks/useAgentStore';
import { Notify } from '../../services/notificationService';
import { setResult } from '../../app/Slices/adminAgentSlice';
import { ContentObject, ContentObjectType } from '../../interfaces/copilotChat';
import MessageGroup from './messages/MessageGroup';
import ChatTitle from '../CopilotBase/ChatTitle';
import PromptInput from '../CopilotBase/PromptInput';
import { AppRoutes } from '../../constants/route';
import { useNavigate } from 'react-router-dom';

type MenuItem = Required<MenuProps>['items'][number];

interface AdminCopilotChatProps {
  addNewChatToThreadList: (threadId: string) => Promise<void>;
  updateThreads: () => Promise<void>;
  newChatFirstMessage: string;
  onShowResults?: () => void;
  isResultPanelVisible: boolean;
  disabled?: boolean;
}

const _doesMsgBelongToSameToolCallGroup = (
  currentMessage: ChatMessage,
  msg: ChatMessage
) => {
  if (msg.role !== currentMessage.role) {
    return false;
  }
  if (!currentMessage.extra?.originData?.content?.tool_call) {
    return false;
  }
  if (!msg.extra?.originData?.content?.tool_call) {
    return false;
  }
  return true;
};

const _findGroupToolCallMsgs = (
  currentMsg: ChatMessage,
  allMsgs: ChatMessage[]
) => {
  if (!allMsgs.length) {
    return [];
  }
  const currentMsgIndex = findIndex(allMsgs, ['id', currentMsg.id]);
  const nearestNonToolCallMsgIndex = findLastIndex(
    allMsgs,
    (m) => {
      return (
        m?.extra?.originData?.content?.type !== ContentObjectType.TOOL_CALL
      );
    },
    currentMsgIndex - 1
  );

  const candidateMsgs = allMsgs.slice(
    Math.min(nearestNonToolCallMsgIndex + 1, currentMsgIndex)
  );
  let foundMsgInGroup = false;
  const msgGroup = [];
  for (const m of candidateMsgs) {
    if (_doesMsgBelongToSameToolCallGroup(currentMsg, m)) {
      msgGroup.push(m);
      foundMsgInGroup = true;
    } else {
      if (foundMsgInGroup) {
        break;
      }
    }
  }
  return msgGroup;
};

const AdminCopilotChat: React.FC<AdminCopilotChatProps> = memo(
  ({
    addNewChatToThreadList,
    updateThreads,
    newChatFirstMessage,
    onShowResults,
    isResultPanelVisible,
    disabled = false,
  }) => {
    const [updatedThread, setUpdatedThread] = useState<boolean>(false);
    const {
      chatRef,
      currentMessage,
      setCurrentMessage,
      isSending,
      handleRequest,
      handleSendMessage,
      onChatChange,
    } = useCopilotChat({
      assistantType: ADMIN_AGENT_ASSISTANT_TYPE,
      threadRun: {},
      setLoadingCallback: undefined,
      handleRequestCallback: (message: string) => {
        if (!updatedThread) {
          updateThreads();
          setUpdatedThread(true);
        }
      },
    });
    const { chatMessages: initialChats } = useAgentSelector(
      (state) => state.adminAgent
    );
    const [showPromptOptions, setShowPromptOptions] = useState<boolean>(false);
    const isFirstMessageRef = useRef(true);
    const hasSentRef = useRef(false);
    const isReadonly = useSelector(selectIsSubscriptionReadonly);
    const [key, setKey] = useState<string>(Date.now().toString());
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const promptOptionsRef = useRef<HTMLDivElement>(null);
    const agentDispatch = useAgentDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const prompts = useAgentSelector((state) => state.promptLibrary.prompts);
    const navigate = useNavigate();

    useEffect(() => {
      if (initialChats.length > 0) {
        setKey(Date.now().toString());
        handleInitialLoading();
      }
    }, [initialChats]);

    useEffect(() => {
      if (newChatFirstMessage && !hasSentRef.current) {
        handleInitialLoading();
        handleSendMessage(newChatFirstMessage);
        hasSentRef.current = true;
      }
    }, [newChatFirstMessage]);

    useEffect(() => {
      let observer: MutationObserver | null = null;

      function setLinksTarget() {
        const chatContainer = document.querySelector(
          '.ant-pro-chat-chat-list-container'
        );
        if (!chatContainer) return;
        const links = chatContainer.querySelectorAll('a');
        links.forEach((link) => {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        });
      }

      // Run initially and whenever messages change
      setLinksTarget();

      // Observe for DOM changes in the chat container
      const chatContainer = document.querySelector(
        '.ant-pro-chat-chat-list-container'
      );
      if (chatContainer) {
        observer = new MutationObserver(setLinksTarget);
        observer.observe(chatContainer, { childList: true, subtree: true });
      }

      // As a fallback, also observe the document for the chat container appearing
      const bodyObserver = new MutationObserver(() => {
        setLinksTarget();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });

      return () => {
        if (observer) observer.disconnect();
        bodyObserver.disconnect();
      };
    }, [initialChats]);

    const handleInitialLoading = useCallback(() => {
      setLoading(false);
      setTimeout(() => {
        handleScrollToBottom();
      }, 1000);
    }, []);

    const handleScrollToBottom = useCallback(() => {
      const chatContainer = document.querySelector(
        '.ant-pro-chat-chat-list-container'
      );
      if (!chatContainer) return;

      const checkAndScroll = () => {
        const isAtBottom =
          Math.abs(
            chatContainer.scrollHeight -
              chatContainer.scrollTop -
              chatContainer.clientHeight
          ) <= 1 && chatContainer.scrollHeight >= chatContainer.clientHeight;

        if (!isAtBottom) {
          const chat = chatRef.current;
          if (chat && typeof chat.scrollToBottom === 'function') {
            chat.scrollToBottom();
          }
        } else {
          clearInterval(scrollInterval);
        }
      };

      const scrollInterval = setInterval(checkAndScroll, 500);
      // Initial check required to ensure the chat container is at the bottom because it sometimes doesn't scroll to the bottom due to customizations of chat items
      checkAndScroll();
    }, [chatRef]);

    const handlePromptSelect = useCallback(
      (value: string) => {
        if (contentEditableRef.current) {
          contentEditableRef.current.textContent = value;
          contentEditableRef.current.focus();
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(contentEditableRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
          setCurrentMessage(value);
        }
        setShowPromptOptions(false);
      },
      [setCurrentMessage]
    );

    const handleSubmit = useCallback(
      (prompt: string) => {
        if (contentEditableRef.current) {
          handleSendMessage(prompt);
          !isSending && (contentEditableRef.current.textContent = '');
        }
      },
      [handleSendMessage, isSending]
    );

    const menuItems: MenuItem[] = useMemo(
      () =>
        prompts.map((prompt) => ({
          key: prompt.key,
          label: prompt.label,
          children: prompt.children.map((child) => ({
            key: child.key,
            label: child.label,
            disabled: child.comingSoon,
            onClick: () => handlePromptSelect(child.value),
          })),
        })),
      [prompts, handlePromptSelect]
    );

    const handleAdminAgentRequest = useCallback(
      async (chatMessages: ChatMessage[]) => {
        const isFirst = chatMessages.length === 1;
        isFirstMessageRef.current = isFirst;
        let threadId = '';
        if (isFirst) {
          let error = null;
          try {
            threadId = await getCopilotChatSessionId(
              ADMIN_AGENT_ASSISTANT_TYPE
            );
          } catch (e) {
            setCopilotChatSessionId(FAILED_THREAD_ID, 7);
            navigate(
              `${AppRoutes.adminAgent.path}/thread/${FAILED_THREAD_ID}`,
              {
                replace: true,
              }
            );
          }
          if (threadId) {
            try {
              setCopilotChatSessionId(threadId, 1);
              addNewChatToThreadList(threadId);
            } catch (e) {
              error = e;
            }
          }

          if (error) {
            Notify.error(
              (error as Error).message || 'An unexpected error occurred.'
            );
          }
        }
        return handleRequest(chatMessages);
      },
      [handleRequest, addNewChatToThreadList, navigate]
    );

    const handleArtifactClick = useCallback(
      (msg: ContentObject) => {
        if (msg) {
          agentDispatch(setResult([msg]));
          onShowResults?.();
        }
      },
      [agentDispatch, onShowResults]
    );

    return (
      <div
        id="admin-copilot"
        className={classNames(
          'copilot',
          'admin-agent-copilot',
          isResultPanelVisible ? 'collapsed' : 'expanded'
        )}
      >
        <ProChat
          key={key}
          loading={loading}
          chatRef={chatRef}
          helloMessage={
            <div>
              <AISvg />
              <span>Welcome to the Admin Agent! How may I help you?</span>
            </div>
          }
          initialChats={initialChats}
          chatItemRenderConfig={useMemo(
            () => ({
              titleRender: (props: ChatItemProps, defaultDom: ReactNode) => {
                return <ChatTitle props={props} defaultDom={defaultDom} />;
              },
              contentRender: (props: ChatItemProps) => {
                const msg = props.originData as ChatMessage;
                const emptyDom = <div className="invisible"></div>;
                if (!msg) {
                  // Case 1: No message -> skip rendering
                  return emptyDom;
                }
                const originalMsg = msg.extra?.originData;
                const thisGroupMsgs = _findGroupToolCallMsgs(msg, initialChats);
                const currentIndexInGroup = findIndex(thisGroupMsgs, [
                  'id',
                  msg.id,
                ]);

                const isMessageNew =
                  findIndex(initialChats, ['id', msg.id]) === -1;

                if (thisGroupMsgs.length > 1) {
                  if (currentIndexInGroup === 0) {
                    // Case 2: The current message is the first message in the group -> render the whole group
                    return (
                      <MessageGroup
                        messages={thisGroupMsgs}
                        handleArtifactClick={handleArtifactClick}
                      />
                    );
                  }
                  // Case 3: The current message is not the first message in the group -> skip rendering the current message because it is already rendered in case 2
                  return emptyDom;
                } else {
                  // Case 4: The current message is not in a group -> render the current message
                  return (
                    <MessageGroup
                      isSingleItemStandalone={
                        originalMsg?.content?.type !==
                        ContentObjectType.TOOL_CALL
                      }
                      collapsible={!isMessageNew}
                      messages={[msg]}
                      handleArtifactClick={handleArtifactClick}
                    />
                  );
                }
              },
            }),
            [initialChats, handleArtifactClick]
          )}
          request={handleAdminAgentRequest}
          locale="en-US"
          showTitle
          userMeta={{
            title: 'User',
          }}
          assistantMeta={{
            title: 'Cirra AI',
          }}
          onChatsChange={(msgs: ChatMessage[]) => onChatChange(msgs)}
        />
        <Tooltip
          title={isReadonly ? getReadonlyElementTitle() : ''}
          destroyOnHidden={false}
        >
          <div className="chat-input-container">
            <PromptInput
              ref={contentEditableRef}
              currentPrompt={currentMessage}
              cancellable
              isLoading={isSending}
              isReadonly={isReadonly}
              disabled={disabled}
              placeholder={
                isReadonly ? getReadonlyElementTitle() : 'Reply to Cirra AI'
              }
              onCurrentPromptChange={setCurrentMessage}
              onSubmit={handleSubmit}
              onShowingPromptTemplateChange={setShowPromptOptions}
            />
            {showPromptOptions && (
              <div
                className="copilot-form-prompt-options"
                ref={promptOptionsRef}
              >
                <Menu
                  mode="inline"
                  items={menuItems}
                  defaultOpenKeys={menuItems
                    .map((item) => item?.key)
                    .filter((key): key is string => key !== undefined)}
                />
              </div>
            )}
          </div>
        </Tooltip>
      </div>
    );
  }
);

AdminCopilotChat.displayName = 'AdminCopilotChat';

export default AdminCopilotChat;
